/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: AI相似照片去重埋点 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-06
 */

import { useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import sensors, { sensorsRole } from '../../../utils/sensors'

/**
 * 埋点事件名称
 */
const TRACK_EVENT_COMPLETED = 'workgroup_b4311_c8018_completed'
const TRACK_EVENT_CLICK = 'workgroup_b4311_c8018_d8019_click'

/**
 * 埋点配置
 */
const TRACK_CONFIG_COMPLETED = {
  requirement_id: 905,
  track_sign: 'aworkgroup.b4311.c8018.completed.workgroup_b4311_c8018_completed'
}

const TRACK_CONFIG_CLICK = {
  requirement_id: 905,
  track_sign:
    'aworkgroup.b4311.c8018.d8019.click.workgroup_b4311_c8018_d8019_click'
}

/**
 * 进入页面埋点参数
 */
interface EnterPageParams {
  groupID: string
  role: string
}

/**
 * 完成检测埋点参数
 */
interface CompleteParams {
  groupID: string
  role: string
  bool1: boolean // 是否按照时间排序
  string1: string // 筛选起始日期
  string2: string // 筛选结束日期
  number2: number // 每页显示的相似组数量
  number1: number // 每个相似组的照片数量
}

/**
 * AI相似照片去重埋点 Hook
 */
export const useSensorTracking = () => {
  const groupId = useSelector((state: any) => state.group.groupId)
  const myRole = useSelector((state: any) => state.group.myRole)

  /**
   * 上报进入页面埋点
   */
  const trackEnterPage = useCallback(() => {
    if (!groupId) return

    sensors.track(TRACK_EVENT_COMPLETED, {
      ...TRACK_CONFIG_COMPLETED,
      actionType: 'enterPage',
      groupID: String(groupId),
      role: sensorsRole(myRole)
    })
  }, [groupId, myRole])

  /**
   * 上报完成检测埋点
   */
  const trackComplete = useCallback(
    (params: {
      sortByTime: boolean
      startDate: string
      endDate: string
      groupsPerPage: number
      photosPerGroup: number
    }) => {
      if (!groupId) return

      sensors.track(TRACK_EVENT_COMPLETED, {
        ...TRACK_CONFIG_COMPLETED,
        actionType: 'complete',
        groupID: String(groupId),
        role: sensorsRole(myRole),
        bool1: params.sortByTime,
        string1: params.startDate,
        string2: params.endDate,
        number2: params.groupsPerPage,
        number1: params.photosPerGroup
      })
    },
    [groupId, myRole]
  )

  /**
   * 自动上报进入页面埋点（组件挂载时）
   */
  useEffect(() => {
    trackEnterPage()
  }, [trackEnterPage])

  /**
   * 上报点击操作埋点
   */
  const trackClick = useCallback(
    (params: {
      actionType:
        | 'enterPage'
        | 'back'
        | 'filter'
        | 'selectAll'
        | 'cancelSelectAll'
        | 'selectGroup'
        | 'cancelSelectGroup'
        | 'delete'
        | 'cancelDelete'
        | 'confirmDelete'
        | 'preview'
        | 'cancelPreview'
        | 'demo'
      bool1?: boolean // 是否筛选用户
      bool2?: boolean // 是否筛选地点
      bool3?: boolean // 是否筛选日期
      bool4?: boolean // 是否筛选水印
      number1?: number // 照片组数
      number2?: number // 重复照片总数
      number3?: number // 选中照片数
    }) => {
      if (!groupId) return

      sensors.track(TRACK_EVENT_CLICK, {
        ...TRACK_CONFIG_CLICK,
        actionType: params.actionType,
        groupID: String(groupId),
        role: sensorsRole(myRole),
        bool1: params.bool1 ?? false,
        bool2: params.bool2 ?? false,
        bool3: params.bool3 ?? false,
        bool4: params.bool4 ?? false,
        number1: params.number1 ?? 0,
        number2: params.number2 ?? 0,
        number3: params.number3 ?? 0
      })
    },
    [groupId, myRole]
  )

  return {
    trackEnterPage,
    trackComplete,
    trackClick
  }
}
